import { ENDPOINTS } from '../utils/endpoints';
import { getAPIwithToken } from '../utils/fetchApi';

export interface FetchOrdersParams {
  page?: number;
  limit?: number;
  status?: string;
  distributorId?: number | string;
}

export interface OrderLineItem {
  id: number | string;
  orderId?: number | string;
  productId?: number;
  productName?: string;
  quantity?: number;
  price?: number;
  totalAmount?: number;
  product?: {
    id?: number;
    name?: string;
    skuCode?: string;
    mrp?: number;
  };
}

export interface BackendOrder {
  id: number | string;
  orderNumber?: string;
  orderId?: string;
  distributorId?: number;
  distributorName?: string;
  distributor?: {
    id?: number;
    name?: string;
    firstName?: string;
    code?: string;
    city?: { name: string };
    zone?: { name: string };
  };
  totalAmount?: number;
  totalQuantity?: number;
  status?: string;
  orderDate?: string;
  createdAt?: string;
  expectedDeliveryDate?: string;
  shippingAddress?: string;
  specialInstructions?: string;
  items?: OrderLineItem[];
  lineItems?: OrderLineItem[];
}

export interface GetOrdersResponse {
  status: string;
  message?: string;
  data: BackendOrder[] | {
    orders?: BackendOrder[];
    pagination?: {
      totalCount?: number;
      total?: number;
      page?: number;
      limit?: number;
      totalPages?: number;
    };
  };
  pagination?: {
    totalCount?: number;
    total?: number;
    page?: number;
    limit?: number;
    totalPages?: number;
  };
}

export const mapBackendOrderToFrontend = (item: BackendOrder): any => {
  const distributorObj = (item as any).distributor || (item as any).Distributor;
  const distributorName =
    distributorObj?.name ||
    distributorObj?.firstName ||
    item.distributorName ||
    `Distributor #${item.distributorId || item.id}`;
  const distributorSapCode = distributorObj?.sapCode || distributorObj?.sapCustomerId || '';

  const zoneName = distributorObj?.zone?.name || item.distributor?.zone?.name || 'All';
  const cityName = distributorObj?.city?.name || item.distributor?.city?.name || 'All';

  const dateStr = item.orderDate || item.createdAt
    ? new Date(item.orderDate || item.createdAt!).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
    : 'N/A';

  const rawItems = item.items || item.lineItems || [];
  const lineItems = rawItems.map((li: any, idx: number) => {
    const unitPrice = Number(li.unitPrice ?? li.price ?? 0);
    const qty = Number(li.quantity ?? li.qty ?? 0);
    const amount = Number(li.totalAmount ?? (unitPrice * qty));
    const schemeObj = li.appliedScheme || li.scheme;
    const schemeName = schemeObj?.name
      ? schemeObj.name
      : typeof li.scheme === 'string'
      ? li.scheme
      : '—';

    const schemeBonus = schemeObj?.freeQuantity ? `+${schemeObj.freeQuantity} Free` : undefined;

    return {
      id: String(li.id || idx),
      name: li.productName || li.product?.name || 'Gowardhan Product',
      sku: li.product?.sapCode || li.product?.skuCode || `SKU-${li.productId || idx}`,
      unitPrice,
      qty,
      unit: 'units',
      amount,
      scheme: schemeName,
      schemeBonus,
      schemeObj: typeof schemeObj === 'object' ? schemeObj : undefined,
      tax: li.tax || 'Inclusive',
      status: item.status || 'PENDING',
      iconBg: 'bg-amber-100',
      iconText: '🥛',
    };
  });

  const firstItemName = lineItems.length > 0 ? lineItems[0].name : 'Gowardhan Order';
  const extraItemsCount = lineItems.length > 1 ? lineItems.length - 1 : undefined;

  // Order-level scheme summary
  let orderSchemeSummary = '—';
  let orderSchemeBonus = undefined;
  const appliedSchemes = (item as any).appliedSchemes || [];
  if (Array.isArray(appliedSchemes) && appliedSchemes.length > 0) {
    orderSchemeSummary = appliedSchemes[0].name;
    if (appliedSchemes.length > 1) {
      orderSchemeBonus = `+${appliedSchemes.length - 1} more scheme(s)`;
    } else if (appliedSchemes[0].freeQuantity) {
      orderSchemeBonus = `+${appliedSchemes[0].freeQuantity} Free`;
    }
  } else if ((item as any).hasSchemeApplied) {
    orderSchemeSummary = 'Scheme Applied';
  }

  let mappedStatus: 'DELIVERED' | 'PARTIAL' | 'DISPATCHED' | 'PENDING' | 'CANCELLED' = 'PENDING';
  const rawStatus = (item.status || '').toUpperCase();
  if (rawStatus === 'DELIVERED' || rawStatus === 'COMPLETED') mappedStatus = 'DELIVERED';
  else if (rawStatus === 'PARTIAL') mappedStatus = 'PARTIAL';
  else if (rawStatus === 'DISPATCHED') mappedStatus = 'DISPATCHED';
  else if (rawStatus === 'CANCELLED') mappedStatus = 'CANCELLED';
  else mappedStatus = 'PENDING';

  return {
    id: String(item.id),
    orderId: item.orderNumber || (item as any).orderId || `ORD-${item.id}`,
    date: dateStr,
    customerName: distributorName,
    distributorSapCode: distributorSapCode,
    zone: zoneName,
    city: cityName,
    itemsSummary: firstItemName,
    extraItemsCount,
    qty: Number(item.totalQuantity || lineItems.reduce((sum: number, i: any) => sum + i.qty, 0)),
    totalAmount: Number(item.totalAmount || lineItems.reduce((sum: number, i: any) => sum + i.amount, 0)),
    scheme: orderSchemeSummary,
    schemeBonus: orderSchemeBonus,
    appliedSchemes: Array.isArray(appliedSchemes) ? appliedSchemes : [],
    incentive: '—',
    status: mappedStatus,
    expectedDeliveryDate: item.expectedDeliveryDate || 'N/A',
    shippingAddress: item.shippingAddress || 'N/A',
    specialInstructions: item.specialInstructions || '',
    lineItems,
    rawBackendData: item,
  };
};

export async function getOrders(params?: FetchOrdersParams): Promise<GetOrdersResponse> {
  let endpoint = ENDPOINTS.GET_ORDERS;

  if (params) {
    const query = new URLSearchParams();
    if (params.page) {
      query.append('page', params.page.toString());
    }
    if (params.limit) {
      query.append('limit', params.limit.toString());
    }
    if (params.status && params.status !== 'All') {
      query.append('status', params.status);
    }
    if (params.distributorId) {
      query.append('distributorId', params.distributorId.toString());
    }
    const queryString = query.toString();
    if (queryString) {
      endpoint += `?${queryString}`;
    }
  }

  return getAPIwithToken<GetOrdersResponse>(endpoint);
}

